import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { platformSettingsAPI } from '../lib/api';

export interface BrandingSettings {
  companyName: string;
  loginTitle: string;
  copyrightOwner: string;
  defaultTheme: number;
  availableThemes: string[];
  backgroundColor: string;
  loginColor: string;
  fontColor: string;
  buttonColor: string;
  companyLogo: string | null;
  companyDarkLogo: string | null;
  website: string;
  supportEmail: string;
  salesEmail: string;
  noReplyEmail: string;
  emailSubject: string;
  emailSignature: string;
}

interface BrandingContextType {
  branding: BrandingSettings;
  updateBranding: (settings: Partial<BrandingSettings>) => void;
  loadBrandingFromBackend: () => Promise<void>;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

const DEFAULT_BRANDING: BrandingSettings = {
  companyName: 'Helix',
  loginTitle: '',
  copyrightOwner: 'Arden Rede Ltd',
  defaultTheme: 1,
  availableThemes: ['theme1', 'theme2'],
  backgroundColor: '#ffffff',
  loginColor: '#4f46e5', // Indigo-600
  fontColor: '#333333',
  buttonColor: '#4f46e5',
  companyLogo: null,
  companyDarkLogo: null,
  website: '',
  supportEmail: '',
  salesEmail: '',
  noReplyEmail: '',
  emailSubject: 'Password Reset',
  emailSignature: 'The Team',
};

/**
 * Provides platform branding settings (company name, colors, emails).
 * Loads from the public branding endpoint on mount -- no authentication required,
 * so the login page can also display branded content.
 */
export const BrandingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [branding, setBranding] = useState<BrandingSettings>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);

  const loadBrandingFromBackend = useCallback(async () => {
    setIsLoading(true);
    try {
      // Use public branding endpoint (no auth required)
      const response = await platformSettingsAPI.getPublicBranding();
      if (response.data) {
        const settings = response.data;
        setBranding(prev => ({
          ...prev,
          companyName: settings.company_name || DEFAULT_BRANDING.companyName,
          loginTitle: settings.login_title || '',
          backgroundColor: settings.background_color || DEFAULT_BRANDING.backgroundColor,
          loginColor: settings.login_color || DEFAULT_BRANDING.loginColor,
          fontColor: settings.font_color || DEFAULT_BRANDING.fontColor,
          buttonColor: settings.button_color || DEFAULT_BRANDING.buttonColor,
          companyLogo: settings.company_logo || null,
          companyDarkLogo: settings.company_dark_logo || null,
        }));
      }
    } catch (error) {
      console.error('Failed to load branding settings:', error);
      // Keep defaults on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load branding on mount - doesn't require auth since login page needs it
  useEffect(() => {
    loadBrandingFromBackend();
  }, [loadBrandingFromBackend]);

  const updateBranding = (settings: Partial<BrandingSettings>) => {
    setBranding(prev => ({ ...prev, ...settings }));
  };

  return (
    <BrandingContext.Provider
      value={{
        branding,
        updateBranding,
        loadBrandingFromBackend,
        isLoading,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

/**
 * Accesses platform branding settings (company name, colors, contact emails).
 * Must be used within a {@link BrandingProvider}.
 */
export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
